import React, { useState, useEffect } from "react";
import {
  Star,
  Layers,
  AlertTriangle,
  CircleDot,
  FolderKanban,
  User,
  Mail,
  UserCheck,
  UserPlus,
  Timer,
  CalendarDays,
  X,
  History,
  PauseCircle,
  RotateCcw,
  CheckCircle,
  Loader,
  CircleSlash,
  Copy,
  MoreVertical,
  Pencil,
} from "lucide-react";
import frameBg from "../../../Assets/FrameBGDashboard.png";
import AttachmentPreviewModal from "../Modal/AttachmentPreviewModal";
import { getTaskHistory, EditTask } from "../../../Services/TaskService";
import { setCopiedTask } from "../../../utlis/CopyTaskStore";
import NotificationAlert from "../../Alerts/NotificationAlert";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../../Utils/urlHelper";

const getStatusInfo = (status) => {
  switch (status) {
    case "Assigned":
      return {
        icon: <UserCheck size={14} className="text-indigo-700" />,
        text: "Assigned",
        style: "bg-indigo-100 text-indigo-700",
      };

    case "In Progress":
      return {
        icon: <Loader size={14} className="text-blue-700 animate-spin" />,
        text: "In Progress",
        style: "bg-blue-100 text-blue-700",
      };

    case "Completed":
      return {
        icon: <CheckCircle size={14} className="text-green-700" />,
        text: "Completed",
        style: "bg-green-100 text-green-700",
      };

    case "Re-open":
      return {
        icon: <RotateCcw size={14} className="text-purple-700" />,
        text: "Re-open",
        style: "bg-purple-100 text-purple-700",
      };

    case "Closed":
      return {
        icon: <CircleSlash size={14} className="text-gray-700" />,
        text: "Closed",
        style: "bg-gray-200 text-gray-700",
      };

    case "Hold":
      return {
        icon: <PauseCircle size={14} className="text-yellow-700" />,
        text: "Hold",
        style: "bg-yellow-100 text-yellow-700",
      };

    default:
      return {
        icon: <CircleDot size={14} className="text-gray-600" />,
        text: status,
        style: "bg-gray-100 text-gray-600",
      };
  }
};

const ViewTaskModal = ({
  show,
  onClose,
  task,
  onOpenHistory,
  onInProgress,
  onCompleted,
  onClosed,
  onReopen,
  onResume,
  onHold,
  onReassign,
  onInvalid,
}) => {
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [isPM, setIsPM] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTask, setEditTask] = useState({
    description: "",
  });
  const [loggedInUser, setLoggedInUser] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    type: "",
    message: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const pm = Cookies.get("Orbit_PMCount");
    const userName = Cookies.get("Orbit_UserName");

    if (pm && Number(pm) > 0) {
      setIsPM(true);
    } else {
      setIsPM(false);
    }

    if (userName) {
      setLoggedInUser(userName);
    }
  }, []);

  useEffect(() => {
    if (show && task) {
      setIsEditMode(false);
      setEditTask({
        description: task.description || "",
      });
    }
  }, [show, task]);

  useEffect(() => {
    if (show) {
      setAlert({
        show: false,
        type: "",
        message: "",
      });
    }
  }, [show]);

  const btn =
    "px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition";
  if (!show || !task) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";

    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadHistory = async () => {
    try {
      const res = await getTaskHistory(task.taskNo);
      if (res.data.status === "Success") {
        const history = res.data.history || [];

        onClose();
        onOpenHistory(history);
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || "Failed to load history."
      });
    }
  };

  const statusInfo = getStatusInfo(task.status);

  const canEditTask =
    (isPM || loggedInUser === task?.assignedBy) &&
    (task.status === "Assigned" || task.status === "In Progress");

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-[#F5FAFC] w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="p-5 text-white shadow-md bg-cover bg-center bg-no-repeat flex justify-between items-center"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div>
              <h2 className="text-xl font-semibold tracking-wide">
                Task Details
              </h2>
              <p className="opacity-90 mt-1">
                <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium">
                  #{task.taskNo}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3 relative">
              {/* Copy Task Button */}
              <button
                onClick={() => {
                  setCopiedTask(task);
                  onClose();
                  window.dispatchEvent(new Event("openCreateTaskWithCopy"));
                }}
                className="bg-white/20 backdrop-blur px-4 py-2 rounded-lg flex items-center gap-2 
             hover:bg-white/30 transition text-white shadow"
              >
                <Copy size={18} /> Copy
              </button>

              {/* View History Button */}
              <button
                onClick={loadHistory}
                className="bg-white/20 backdrop-blur px-4 py-2 rounded-lg flex items-center gap-2 
               hover:bg-white/30 transition text-white shadow"
              >
                <History size={18} /> View History
              </button>

              {/* Three Dot Menu */}
              {canEditTask && (
                <button
                  onClick={() => setShowMenu((prev) => !prev)}
                  className="bg-white/20 backdrop-blur p-2 rounded-lg 
    hover:bg-white/30 transition text-white shadow"
                >
                  <MoreVertical size={18} />
                </button>
              )}

              {/* Dropdown */}
              {showMenu && canEditTask && (
                <div
                  className="absolute right-0 top-12 bg-white text-gray-800 
                 rounded-lg shadow-lg w-40 z-50 border"
                >
                  <button
                    onClick={() => {
                      setIsEditMode(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-sm flex items-center gap-2 
                   hover:bg-gray-100 rounded-t-lg"
                  >
                    <Pencil size={16} className="text-blue-600" />
                    Edit Task
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* DESCRIPTION */}
            {isEditMode ? (
              <>
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Task Description
                </label>

                <textarea
                  value={editTask.description || ""}
                  maxLength={500}
                  rows={8}
                  onChange={(e) =>
                    setEditTask({ ...editTask, description: e.target.value })
                  }
                  className="w-full px-4 py-3 text-lg font-medium border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-[#018aec] focus:border-[#018aec]"
                  placeholder="Edit task description (maximum 500 characters)"
                />

                <p className="text-sm text-gray-500 mt-1 text-right">
                  {editTask.description.length}/500 characters
                </p>
              </>
            ) : (
              <>
                <p className="text-xl font-semibold text-gray-900">
                  {task.description}
                </p>

                {/* STATUS */}
                <div className="mt-4 mb-6 flex items-center justify-between">
                  {/* Logo*/}
                  <div className="flex items-center gap-3">
                    {task.logoURL ? (
                      <img
                        src={getImageUrl(task.logoURL)}
                        alt="Project Logo"
                        className="w-36 h-12 object-contain rounded-lg border bg-white p-1 shadow"
                      />
                    ) : (
                      <span
                        className={`px-3 py-2 rounded-full text-sm font-medium flex items-center gap-2 w-fit ${statusInfo.style}`}
                      >
                        {statusInfo.icon}
                        {statusInfo.text}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  {task.logoURL && (
                    <span
                      className={`px-3 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${statusInfo.style}`}
                    >
                      {statusInfo.icon}
                      {statusInfo.text}
                    </span>
                  )}
                </div>

                {/* GRID DETAILS */}
                <div className="grid grid-cols-2 gap-6">
                  <DetailCard
                    label="Project Name"
                    value={task.projectName}
                    icon={FolderKanban}
                  />

                  <DetailCard
                    label="Severity"
                    value={task.severity}
                    icon={AlertTriangle}
                    severityChip
                  />

                  <DetailCard
                    label="Assigned By"
                    email={task.assignedByEmail}
                    name={task.assignedBy}
                    icon={UserCheck}
                    full
                  />

                  <DetailCard
                    label="Assigned To"
                    email={task.assignedToEmail}
                    name={task.assignedTo}
                    icon={UserPlus}
                    full
                  />

                  <DetailCard
                    label="Created At"
                    value={formatDateTime(task.createdAt)}
                    icon={Timer}
                  />

                  <DetailCard
                    label="Expected Completion"
                    value={formatDateTime(task.estimatedComplete)}
                    icon={CalendarDays}
                  />

                  <DetailCard
                    label="Expected Completion By Dev"
                    value={formatDateTime(task.estimatedCompleteByDev)}
                    icon={CalendarDays}
                  />

                  <DetailCard
                    label="Reopen Count"
                    value={task.reopenCount}
                    icon={History}
                  />
                </div>

                {/* ATTACHMENTS */}
                {task.attachmentUrls?.length > 0 && (
                  <div className="mt-6 p-5 bg-white rounded-xl border shadow-sm">
                    <h3 className="text-gray-800 font-semibold mb-4">
                      Attachments:
                    </h3>

                    <div className="flex flex-wrap gap-3">
                      {task.attachmentUrls.map((url, i) => (
                        <button
                          key={i}
                          onClick={() => setPreviewUrl(url)}
                          className="px-6 py-2 text-sm bg-gradient-to-r 
                           from-[#0061A4] to-[#00ACED] 
                           text-white rounded-md shadow-md 
                           hover:opacity-90 transition"
                        >
                          Attachment {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ACTION BUTTONS */}
                {task.status !== "Closed" && (
                  <div className="mt-6 p-5 bg-white rounded-xl border shadow-sm">
                    <h3 className="text-gray-800 font-semibold mb-4">
                      Available Actions
                    </h3>

                    {/* ASSIGNED */}
                    {task.status === "Assigned" && (
                      <div className="flex gap-3 flex-wrap">
                        <button
                          onClick={() => {
                            onClose();
                            onInProgress(task);
                          }}
                          className={btn}
                        >
                          In Progress
                        </button>
                        <button
                          onClick={() => {
                            onClose();
                            onHold(task);
                          }}
                          className={btn}
                        >
                          Hold
                        </button>
                        {isPM && (
                          <button
                            onClick={() => {
                              onClose();
                              onReassign(task);
                            }}
                            className={btn}
                          >
                            Re-Assign Task
                          </button>
                        )}
                        {isPM && (
                          <button
                            onClick={() => {
                              onClose();
                              onInvalid(task);
                            }}
                            className={btn}
                          >
                            Invalid Task
                          </button>
                        )}
                      </div>
                    )}

                    {/* IN PROGRESS */}
                    {task.status === "In Progress" && (
                      <div className="flex gap-3 flex-wrap">
                        <button
                          onClick={() => {
                            onClose();
                            onCompleted(task);
                          }}
                          className={btn}
                        >
                          Mark Completed
                        </button>
                        <button
                          onClick={() => {
                            onClose();
                            onHold(task);
                          }}
                          className={btn}
                        >
                          Hold
                        </button>
                        {isPM && (
                          <button
                            onClick={() => {
                              onClose();
                              onReassign(task);
                            }}
                            className={btn}
                          >
                            Re-Assign Task
                          </button>
                        )}
                        {isPM && (
                          <button
                            onClick={() => {
                              onClose();
                              onInvalid(task);
                            }}
                            className={btn}
                          >
                            Invalid Task
                          </button>
                        )}
                      </div>
                    )}

                    {/* COMPLETED */}
                    {task.status === "Completed" &&
                      (task.isPM ? (
                        <div className="flex gap-3 flex-wrap">
                          <button
                            onClick={() => {
                              onClose();
                              onClosed(task);
                            }}
                            className={btn}
                          >
                            Close
                          </button>
                          <button
                            onClick={() => {
                              onClose();
                              onReopen(task);
                            }}
                            className={btn}
                          >
                            Re-open
                          </button>
                          <button
                            onClick={() => {
                              onClose();
                              onHold(task);
                            }}
                            className={btn}
                          >
                            Hold
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          No actions available.
                        </div>
                      ))}

                    {/* RE-OPEN */}
                    {task.status === "Re-open" && (
                      <div className="flex gap-3 flex-wrap">
                        <button
                          onClick={() => {
                            onClose();
                            onInProgress(task);
                          }}
                          className={btn}
                        >
                          In Progress
                        </button>
                        <button
                          onClick={() => {
                            onClose();
                            onHold(task);
                          }}
                          className={btn}
                        >
                          Hold
                        </button>
                        {isPM && (
                          <button
                            onClick={() => {
                              onClose();
                              onReassign(task);
                            }}
                            className={btn}
                          >
                            Re-Assign Task
                          </button>
                        )}
                      </div>
                    )}

                    {/* HOLD */}
                    {task.status === "Hold" && (
                      <div className="flex gap-3 flex-wrap">
                        <button
                          onClick={() => {
                            onClose();
                            onResume(task);
                          }}
                          className={btn}
                        >
                          Resume
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* FOOTER */}
          <div className="p-4 bg-gray-100 border-t flex justify-end gap-3">
            {isEditMode ? (
              <>
                <button
                  onClick={() => {
                    setEditTask({ description: task.description });
                    setIsEditMode(false);
                  }}
                  className="px-6 py-2 bg-gray-200 rounded-md"
                >
                  Cancel
                </button>

                <button
                  onClick={async () => {
                    try {
                      if (!editTask.description.trim()) {
                        setAlert({
                          show: true,
                          type: "fail",
                          message: "Task description cannot be empty",
                        });
                        return;
                      }

                      const payload = {
                        taskUID: task.taskUID,
                        description: editTask.description,
                      };

                      const res = await EditTask(payload);

                      if (res?.data?.status === "Success") {
                        task.description = editTask.description;

                        setAlert({
                          show: true,
                          type: "success",
                          message: res.data.message,
                        });

                        setIsEditMode(false);
                      } else {
                        setAlert({
                          show: true,
                          type: "fail",
                          message:
                            res?.data?.message || "Failed to update task",
                        });
                      }
                    } catch (error) {
                      setAlert({
                        show: true,
                        type: "fail",
                        message:
                          error?.response?.data?.message ||
                          "Something went wrong while updating task",
                      });
                    }
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm bg-white border rounded-md hover:bg-gray-200 transition"
              >
                Close
              </button>
            )}
          </div>
        </div>
        <NotificationAlert
          show={alert.show}
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      </div>

      <AttachmentPreviewModal
        show={!!previewUrl}
        url={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </>
  );
};

export default ViewTaskModal;

const DetailCard = ({
  label,
  value,
  icon: Icon,
  chip,
  severityChip,
  email,
  name,
  full,
}) => (
  <div
    className={`p-4 bg-gray-50 border rounded-xl shadow-sm hover:shadow-md transition-all ${
      full ? "col-span-2" : ""
    }`}
  >
    {/* label */}
    <p className="text-gray-500 text-sm mb-1 flex items-center gap-2">
      {Icon && <Icon size={16} className="text-[#0061A4]" />}
      {label}
    </p>

    {email && name && (
      <div className="text-gray-800 font-medium break-all">
        <p>{email}</p>
        <p className="text-gray-600 text-sm">({name})</p>
      </div>
    )}

    {!email && !name && !chip && !severityChip && (
      <p className="text-gray-800 font-medium break-all">
        {value !== undefined && value !== null ? value : "—"}
      </p>
    )}

    {chip && (
      <span className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium break-all">
        {value}
      </span>
    )}

    {severityChip && (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          value === "High"
            ? "bg-red-100 text-red-700"
            : value === "Moderate"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-green-100 text-green-700"
        }`}
      >
        {value}
      </span>
    )}
  </div>
);
